import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { useLocation } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { log } from "@/lib/logger";

function RouteFallback({ error, resetErrorBoundary }: FallbackProps) {
  const err = error as Error;
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Não foi possível carregar esta página</h2>
          <p className="text-sm text-muted-foreground">
            Ocorreu um erro ao renderizar este conteúdo.
          </p>
          {import.meta.env.DEV && (
            <pre className="mt-2 text-xs text-left bg-muted p-2 rounded overflow-auto max-h-32">
              {err?.message}
            </pre>
          )}
        </div>
        <Button onClick={resetErrorBoundary} size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    </div>
  );
}

export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={RouteFallback}
      resetKeys={[location.pathname]}
      onError={(error: Error, info) => {
        log.error(
          {
            err: { message: error.message, stack: error.stack },
            componentStack: info.componentStack,
            path: location.pathname,
          },
          "react.route.boundary"
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
